import type { Part, PropertyConfigCode } from "@/features/sub-families/types";
import { getSelectedFinishingColours } from "@/features/sub-families/utils/colours.ts";
import { getPartDescriptor } from "@/features/sub-families/utils/parts.ts";
import { smartNumberChangeMultipleMappingAction } from "@/redux/slices/smart-number-slice.ts";
import { useAppDispatch, useAppSelector } from "@/redux/store.ts";

export default function useAllParts() {
    const dispatch = useAppDispatch();
    const parts = useAppSelector((state) => state.parts.items);
    const smartNumberMapping = useAppSelector((state) => state.smartNumber.mapping);
    const allowAllParts = useAppSelector((state) => state.parts.allowAllParts);

    const allPartsColours = getCommonColours(parts, allowAllParts);

    const hasAllParts = allPartsColours.length > 0;

    // Find the colour that is selected on every part
    const selectedFinishingColours = getSelectedFinishingColours(smartNumberMapping);
    const selectedColourCodes = Object.values(selectedFinishingColours);

    const selectedColourCode = new Set(selectedColourCodes).size === 1 ? selectedColourCodes[0] : null;

    const selectAllPartsFinishingColour = (configCode: PropertyConfigCode) => {
        const descriptors = parts.map(getPartDescriptor);

        const mappings = descriptors.map((descriptor) => ({ descriptor, configCode }));
        dispatch(smartNumberChangeMultipleMappingAction(mappings));
    };

    return { hasAllParts, allPartsColours, selectedColourCode, selectAllPartsFinishingColour };
}

function getCommonColours(parts: Part[], allowAllParts: boolean) {
    if (!allowAllParts) return [];

    const firstPartColours = parts[0]?.colors ?? [];

    // Find the colours that are available on all parts
    // We use the first part here because if a colour exist on every part except the first, it is not available on all parts anyway
    return (
        firstPartColours.filter((firstPartColour) => {
            return parts.every((part) => {
                return part.colors.some((colour) => colour.colourId === firstPartColour.colourId);
            });
        }) ?? []
    );
}
